import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';

const CURRENT_YEAR = new Date().getFullYear();

const FooterHeading = ({ children }: { children: React.ReactNode }) => (
  <p className="text-orange font-roboto-medium mb-5 text-xs uppercase">{children}</p>
);

const linkCls =
  'text-dark-blue/60 hover:text-orange block py-1 text-sm transition-colors duration-200';

const Footer = () => {
  const refs = {
    footer: useRef<HTMLElement>(null),
    cols: useRef<(HTMLDivElement | null)[]>([]),
    bottom: useRef<HTMLDivElement>(null),
  };

  const setColRef = (el: HTMLDivElement | null, index: number) => {
    refs.cols.current[index] = el;
  };

  useGSAP(() => {
    if (!refs.footer.current) return;

    const colEls = refs.cols.current.filter((c): c is HTMLDivElement => c !== null);
    const targets = [...colEls, refs.bottom.current].filter(Boolean);

    gsap.set(targets, { autoAlpha: 0, y: 16 });
    gsap.to(targets, {
      autoAlpha: 1,
      y: 0,
      stagger: 0.09,
      duration: MOTION.durationMd,
      ease: MOTION.ease,
      scrollTrigger: {
        trigger: refs.footer.current,
        start: 'top 90%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  return (
    <footer ref={refs.footer} className="max-width-white-section">
      <div className="max-w-custom mx-auto px-[clamp(20px,5vw,100px)]">
        <div className="border-dark-blue/[0.07] grid grid-cols-2 gap-8 border-t py-16 md:grid-cols-4">
          <div ref={(el) => setColRef(el, 0)} className="col-span-2 md:col-span-1">
            <Image
              alt="Logo zLawyer"
              className="mb-5 h-10 w-auto"
              height={40}
              src={withBasePath('/images/logos/zeleLogo.png')}
              width={100}
            />
            <p className="sr-only">
              Le logiciel de gestion pensé pour les cabinets d&apos;avocats.
            </p>
            <address className="not-italic">
              <p className="text-dark-blue/60 mb-3 text-xs leading-relaxed">
                Zele Solutions
                <br />
                9, rue Angélique Vérien
                <br />
                92200, Neuilly-sur-Seine
              </p>
              <a className={linkCls} href="tel:330184257027">
                01 84 25 70 27
              </a>
              <a className={linkCls} href="mailto:info@zele-solutions.fr">
                info@zele-solutions.fr
              </a>
            </address>
          </div>

          {/* Col 2 : Application */}
          <div ref={(el) => setColRef(el, 1)}>
            <FooterHeading>Application</FooterHeading>
            <div className="flex flex-col gap-3">
              <a
                href="https://apps.apple.com/fr/app/zlawyer/id1174916635?mt=8"
                rel="noreferrer"
                target="_blank"
              >
                <Image
                  alt="Télécharger sur l'App Store"
                  className="cursor-pointer opacity-70 transition-opacity duration-200 hover:opacity-100"
                  height={40}
                  src={withBasePath('/images/logos/badgeAppStore.svg')}
                  style={{ height: '2.5rem', width: 'auto' }}
                  width={130}
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.zelesolution.zlawyer&hl=fr&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
                rel="noreferrer"
                target="_blank"
              >
                <Image
                  alt="Disponible sur Google Play"
                  className="cursor-pointer opacity-70 transition-opacity duration-200 hover:opacity-100"
                  height={40}
                  src={withBasePath('/images/logos/badgePlayStore.svg')}
                  style={{ height: '2.5rem', width: 'auto' }}
                  width={130}
                />
              </a>
            </div>
          </div>

          {/* Col 3 : Ressources */}
          <div ref={(el) => setColRef(el, 2)}>
            <FooterHeading>Ressources</FooterHeading>
            <a
              className={linkCls}
              href="https://download.teamviewer.com/download/TeamViewerQS.exe"
              download
            >
              Assistance (TeamViewer)
            </a>
            <a
              className={linkCls}
              href="https://anydesk.com/fr/downloads"
              rel="noreferrer"
              target="_blank"
            >
              Assistance (AnyDesk)
            </a>
            <p className="hidden">
              Ce fichier est l&apos;installateur officiel de zLawyer, un logiciel destine a la
              facturation pour avocat. Il necessite Windows et n&apos;altere aucun fichier systeme
              sans autorisation explicite.
            </p>
            <a
              className={linkCls}
              href={withBasePath('/download/zlawyerstp.exe')}
              rel="noopener noreferrer"
              download
            >
              Installer zLawyer
            </a>
          </div>

          {/* Col 4 : Légal */}
          <div ref={(el) => setColRef(el, 3)}>
            <FooterHeading>Légal</FooterHeading>
            <Link className={linkCls} href="/conditions-generales-utilisation/" scroll={false}>
              Conditions d&apos;utilisation
            </Link>
            <Link className={linkCls} href="/politique-de-confidentialite/" scroll={false}>
              Politique de confidentialité
            </Link>
            <Link className={linkCls} href="/politique-de-cookies/" scroll={false}>
              Politique de cookies
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div ref={refs.bottom} className="border-dark-blue/[0.07] border-t py-6">
          <p className="text-dark-blue/40 text-xs">
            © 2006 – {CURRENT_YEAR} · Zele Solutions. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
